'use client'

import React from 'react'
import { useTranslations } from 'next-intl'

import type { HolidaySection } from '@/src/types/holidays'
import type { RefundBand } from '@/src/utils/holidays/parseRefundTimeline'

import HolidaySectionWrapper from '../holidaySection'

import BeforeYouBook, { type BeforeYouBookTab } from './beforeYouBook'

/** Which tab each section belongs under. Anything unlisted falls to the terms tab. */
const TAB_OF: Record<string, 'visa' | 'payment' | 'terms'> = {
    'visa-requirements': 'visa',
    notes: 'visa',
    'payment-terms': 'payment',
    'cancellation-policy': 'payment',
    terms: 'terms'
}

const TAB_ORDER: Array<'visa' | 'payment' | 'terms'> = ['visa', 'payment', 'terms']

interface BeforeYouBookWrapperProps {
    sections: HolidaySection[]
    collectionSlug: string
    isMobile: boolean
    /** Refund schedule the page resolved, and which section it belongs to. */
    refundTimeline?: { sectionId: string; bands: RefundBand[]; origin: string }
}

const BeforeYouBookWrapper: React.FC<BeforeYouBookWrapperProps> = ({
    sections,
    collectionSlug,
    isMobile,
    refundTimeline
}) => {
    const t = useTranslations('holidaysPage')

    const labels = {
        visa: t('beforeVisaTab'),
        payment: t('beforePaymentTab'),
        terms: t('beforeTermsTab')
    }

    const grouped = new Map<string, HolidaySection[]>()
    for (const section of sections) {
        const key = TAB_OF[section.id] ?? 'terms'
        const bucket = grouped.get(key)
        if (bucket) bucket.push(section)
        else grouped.set(key, [section])
    }

    // The section bodies are the ones the page already uses, so a refund timeline or a payment
    // stepper still renders as itself — only the chrome around them changes.
    const tabs: BeforeYouBookTab[] = TAB_ORDER.filter(key => grouped.has(key)).map(key => ({
        id: key,
        label: labels[key],
        body: (grouped.get(key) ?? []).map((section, index) => (
            <HolidaySectionWrapper
                key={section.id}
                section={section}
                collectionSlug={collectionSlug}
                variant="flat"
                isFirst={index === 0}
                refundBands={refundTimeline?.sectionId === section.id ? refundTimeline.bands : undefined}
                refundReplacesBody={refundTimeline?.origin === 'cancellation'}
                isMobile={isMobile}
            />
        ))
    }))

    if (!tabs.length) return null

    return <BeforeYouBook tabs={tabs} isMobile={isMobile} />
}

export { BeforeYouBookWrapper }
export default BeforeYouBookWrapper
